import { useEffect, useState } from "react";
import { supabase } from "../auth";
import {
  createAttribute,
  deleteAttribute,
  listAttributesForPlant,
} from "../db/plantAttributes";
import type { PlantAttributeRow } from "@garden/types/db";
import "./PlantAttributesTab.css";

interface Props {
  plantId: string;
}

export function PlantAttributesTab({ plantId }: Props) {
  const [rows, setRows] = useState<PlantAttributeRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const reload = async () => {
    if (!supabase) return;
    try {
      const data = await listAttributesForPlant(supabase, plantId);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void reload();
  }, [plantId]);

  const onCreated = () => {
    setShowForm(false);
    void reload();
  };

  const onDelete = async (id: string) => {
    if (!supabase) return;
    if (!confirm("Delete this attribute?")) return;
    try {
      await deleteAttribute(supabase, id);
      void reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (rows === null && !error) {
    return <div className="AttrTab__loading">Loading…</div>;
  }

  return (
    <div className="AttrTab">
      {rows && rows.length > 0 && (
        <ul className="AttrTab__list">
          {rows.map((a) => (
            <li key={a.id} className="AttrTab__row">
              <span className="AttrTab__k">{a.key}</span>
              <span className="AttrTab__v">{a.value ?? "—"}</span>
              <button
                type="button"
                className="AttrTab__del"
                onClick={() => onDelete(a.id)}
                aria-label={`Delete ${a.key}`}
                title="Delete"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {!showForm && (
        <button
          type="button"
          className="AttrTab__add"
          onClick={() => setShowForm(true)}
        >
          + Add attribute
        </button>
      )}

      {showForm && (
        <AttributeForm
          plantId={plantId}
          onCreated={onCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {error && <p className="AttrTab__error">{error}</p>}

      <p className="AttrTab__hint">
        Use sparingly in v1 — most care info lives in Notes. Use these for
        things you want to filter on later (e.g. all full-sun plants).
      </p>
    </div>
  );
}

function AttributeForm({
  plantId,
  onCreated,
  onCancel,
}: {
  plantId: string;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    if (key.trim() === "") return;
    setSaving(true);
    setError(null);
    try {
      await createAttribute(supabase, {
        plant_id: plantId,
        key: key.trim(),
        value: value.trim() === "" ? null : value.trim(),
        notes: notes.trim() === "" ? null : notes.trim(),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <form className="AttrForm" onSubmit={onSubmit}>
      <div className="AttrForm__row">
        <label className="AttrForm__field">
          <span>Key</span>
          <input
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="e.g. Sun, Hardiness, Spacing"
            required
            maxLength={80}
            autoFocus
          />
        </label>
        <label className="AttrForm__field">
          <span>Value</span>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. Full / part shade"
            maxLength={400}
          />
        </label>
      </div>
      <label className="AttrForm__field">
        <span>Notes (optional)</span>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any useful context"
        />
      </label>
      {error && <p className="AttrForm__error">{error}</p>}
      <div className="AttrForm__actions">
        <button type="button" className="AttrForm__btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="submit"
          className="AttrForm__btn AttrForm__btn--primary"
          disabled={saving || key.trim() === ""}
        >
          {saving ? "Saving…" : "Add"}
        </button>
      </div>
    </form>
  );
}
